import React, { useRef, useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Icon from "../../../components/AppIcon";
import { TimelineStep } from "../types";

const HowItWorksSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeStep, setActiveStep] = useState(0);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const timelineSteps: TimelineStep[] = [
    {
      id: "signup",
      step: 1,
      title: "Sign Up & Verify",
      description:
        "Create your account and verify with College ID/Aadhaar. Workers sign up for free. Businesses choose a subscription plan (₹199, ₹399, or ₹599/month) with monthly task limits of ₹3,000, ₹6,000, and ₹9,000 respectively and 3-month carry-over.",
      icon: "UserPlus",
      color: "text-success",
    },
    {
      id: "post-task",
      step: 2,
      title: "Post Task & AI Invoice",
      description:
        "Businesses post tasks with details. Our AI generates an upfront estimated invoice using real-time online price data with a smart location-based buffer: +20% in metro cities (Bangalore, Hyderabad, Chennai, Delhi, Mumbai), +15% in major Tier-2 cities, and +12% in Tirupati and similar locations. Delivery fees start from ₹20 (0-1km) up to ₹75 (10+ km), dynamically adjusted within 80-120% of base.",
      icon: "FileText",
      color: "text-primary",
    },
    {
      id: "approve-escrow",
      step: 3,
      title: "Approve & Escrow Lock",
      description:
        "Business approves the AI-generated invoice. Payment is locked in secure escrow. For each task, posters select a single store in V1, so distance, delivery fee, and receipt verification are simple and transparent. Worker accepts and begins the task with milestone tracking.",
      icon: "Lock",
      color: "text-secondary",
    },
    {
      id: "complete-task",
      step: 4,
      title: "Complete & Report",
      description:
        "Worker completes the task, uploads receipt, and reports completion. System compares actual bill vs estimated invoice. Unused buffer and delivery fee adjustments calculated automatically using the formula: Final Delivery Fee = Base Fee × (Actual Cost / Estimated Cost).",
      icon: "CheckCircle",
      color: "text-text-secondary",
    },
    {
      id: "refund-payment",
      step: 5,
      title: "Automatic Refund & Payment",
      description:
        "If actual bill is less than estimate, automatic refund credited. Dynamic delivery fee refunded proportionally (floor 80%, ceiling 120% of base). Escrow is released within about 2 hours after completion once delivery is confirmed, receipt verified, and no dispute raised. Payment sent to worker via instant UPI.",
      icon: "Wallet",
      color: "text-success",
    },
  ];

  // Transform scroll progress to step activation
  const stepProgress = useTransform(
    scrollYProgress,
    [0.2, 0.8],
    [0, timelineSteps.length - 1]
  );

  useEffect(() => {
    const unsubscribe = stepProgress.onChange((value) => {
      setActiveStep(Math.floor(value));
    });
    return unsubscribe;
  }, [stepProgress]);

  return (
    <section
      id="how-it-works"
      ref={sectionRef}
      className="py-20 bg-gradient-to-b from-muted/30 to-background relative overflow-hidden"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-body-medium mb-6"
          >
            <Icon name="Cog" size={16} />
            <span>How It Works</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl md:text-4xl lg:text-5xl font-heading-bold text-foreground mb-6"
          >
            AI-Powered, Transparent, and
            <span className="text-primary"> Fair</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-lg text-text-secondary max-w-2xl mx-auto"
          >
            Our AI generates upfront price estimates, calculates dynamic delivery fees, and automatically refunds any overpayment. Complete transparency at every step.
          </motion.p>
        </div>

        {/* Timeline */}
        <div className="max-w-6xl mx-auto">
          {/* Desktop Timeline */}
          <div className="hidden lg:block relative">
            {/* Central Timeline Line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-border transform -translate-x-1/2" />
            <motion.div
              className="absolute left-1/2 top-0 w-1 bg-primary transform -translate-x-1/2"
              initial={{ height: "0%" }}
              animate={{
                height: `${((activeStep + 1) / timelineSteps.length) * 100}%`,
              }}
              transition={{ duration: 0.8 }}
            />

            {/* Timeline Steps */}
            <div className="space-y-24">
              {timelineSteps.map((step, index) => (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className={`flex items-center ${index % 2 === 0 ? "flex-row" : "flex-row-reverse"
                    }`}
                >
                  {/* Content */}
                  <div
                    className={`flex-1 ${index % 2 === 0 ? "pr-12 text-right" : "pl-12 text-left"
                      }`}
                  >
                    <motion.div
                      whileHover={{ y: -4, boxShadow: "0 20px 40px rgba(3,3,3,0.10)" }}
                      transition={{ type: "spring", stiffness: 200, damping: 20 }}
                      className={`inline-block bg-card border border-border rounded-2xl p-8 shadow-card-hover max-w-md ${activeStep >= index ? "ring-2 ring-primary/20" : ""
                        }`}
                    >
                      <div
                        className={`flex items-center space-x-3 mb-4 ${index % 2 === 0
                          ? "flex-row-reverse space-x-reverse"
                          : ""
                          }`}
                      >
                        <div
                          className={`w-12 h-12 rounded-xl flex items-center justify-center ${activeStep >= index
                            ? "bg-gradient-to-br from-primary to-accent text-white"
                            : "bg-muted text-text-secondary"
                            }`}
                        >
                          <Icon name={step.icon} size={24} />
                        </div>
                        <div>
                          <div className="text-sm text-text-secondary font-body-medium">
                            Step {step.step}
                          </div>
                          <h3 className="text-xl font-heading-bold text-foreground">
                            {step.title}
                          </h3>
                        </div>
                      </div>
                      <p className="text-text-secondary leading-relaxed">
                        {step.description}
                      </p>
                    </motion.div>
                  </div>

                  {/* Central Node */}
                  <div className="relative z-10">
                    <motion.div
                      className={`w-6 h-6 rounded-full border-4 ${activeStep >= index
                        ? "bg-primary border-primary shadow-lg"
                        : "bg-background border-border"
                        }`}
                      animate={{
                        scale: activeStep >= index ? [1, 1.2, 1] : 1,
                      }}
                      transition={{
                        duration: 0.6,
                        repeat: activeStep >= index ? Infinity : 0,
                        repeatDelay: 2,
                      }}
                    />
                  </div>

                  {/* Spacer */}
                  <div className="flex-1" />
                </motion.div>
              ))}
            </div>
          </div>

          {/* Mobile Timeline */}
          <div className="lg:hidden space-y-8">
            {timelineSteps.map((step, index) => (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`flex items-start space-x-4 p-6 rounded-2xl transition-all ${activeStep >= index
                  ? "bg-primary/5 border-2 border-primary/20"
                  : "bg-card border border-border"
                  }`}
              >
                {/* Step Icon */}
                <div
                  className={`w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 ${activeStep >= index
                    ? "bg-gradient-to-br from-primary to-accent text-white shadow-lg"
                    : "bg-muted text-text-secondary"
                    }`}
                >
                  <Icon name={step.icon} size={24} />
                </div>

                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-sm text-text-secondary font-body-medium">
                      Step {step.step}
                    </span>
                    <div
                      className={`w-2 h-2 rounded-full ${activeStep >= index ? "bg-primary" : "bg-border"
                        }`}
                    />
                  </div>
                  <h3 className="text-lg font-heading-bold text-foreground mb-2">
                    {step.title}
                  </h3>
                  <p className="text-text-secondary text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Process Highlights */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center p-6 bg-card border border-border rounded-2xl hover:shadow-card-hover transition-card"
          >
            <div className="w-16 h-16 bg-gradient-to-br from-success/10 to-success/5 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon name="Shield" size={32} className="text-success" />
            </div>
            <h4 className="font-heading-bold text-foreground mb-2">
              AI Invoice Generation
            </h4>
            <p className="text-text-secondary text-sm">
              AI generates upfront price estimates using real-time online data with location-based buffers: +20% metro, +15% Tier-2, +12% Tirupati
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-center p-6 bg-card border border-border rounded-2xl hover:shadow-card-hover transition-card"
          >
            <div className="w-16 h-16 bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon name="Zap" size={32} className="text-primary" />
            </div>
            <h4 className="font-heading-bold text-foreground mb-2">
              Dynamic Delivery Fees
            </h4>
            <p className="text-text-secondary text-sm">
              Base fees by distance (₹20-₹75) dynamically adjusted by actual vs estimated cost, with 80-120% safety range
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-center p-6 bg-card border border-border rounded-2xl hover:shadow-card-hover transition-card"
          >
            <div className="w-16 h-16 bg-gradient-to-br from-secondary/10 to-secondary/5 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon name="Lock" size={32} className="text-secondary" />
            </div>
            <h4 className="font-heading-bold text-foreground mb-2">
              Automatic Refunds
            </h4>
            <p className="text-text-secondary text-sm">
              Escrow refunds overpayments within ~2 hours. Buffer and delivery fees recalculated; rest paid to worker via instant UPI
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
